import { ConfiguratorFieldComponent } from "src/components/views/erp/configurator/configuratorForm/components/field/ConfiguratorFieldComponent.tsx";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import React, { useEffect, useState } from "react";
import { setComponentCurrentValue } from "src/components/views/erp/configurator/configuratorForm/utils/configuratorFormUtils.tsx";
import { Box } from "@mui/material";
import { NonLayoutTransformedConfigurationComponent } from "src/components/views/erp/configurator/configuratorForm/components/types/NonLayoutTransformedConfigurationComponent.tsx";
import { mergeSx } from "src/utils/styles.ts";
import { ConfiguratorTextComponent } from "./text/ConfiguratorTextComponent";
import {
	ConfiguratorInput_InputValue,
	ConfiguratorInput_InputValue_Scalar,
} from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { SxProps, Theme } from "@mui/material/styles";

export interface ConfiguratorComponentProps {
	apiRef: React.RefCallback<ConfiguratorComponentApi>;
	component: NonLayoutTransformedConfigurationComponent;
	onSubmit: (value: ConfiguratorInput_InputValue_Scalar) => Promise<unknown>;
	configuratorType: PartConfiguratorType;
	disabled: boolean;
	contextMenu?: () => React.ReactNode;
	sx?: SxProps<Theme>;
}

export interface ConfiguratorComponentApi {
	setValue: (value: ConfiguratorInput_InputValue) => void;
}

export const ConfiguratorComponent = (props: ConfiguratorComponentProps) => {
	const { component } = props;
	if (!component.visible) return null;

	return (
		<LayoutWrapper {...props}>
			<ConfiguratorComponentInner {...props} />
		</LayoutWrapper>
	);
};

const LayoutWrapper = ({
	children,
	component,
	contextMenu,
	sx,
}: ConfiguratorComponentProps & React.PropsWithChildren) => {
	const layout = component.layout;
	const [openContextMenu, contextMenuState] = useContextMenu();
	return (
		<Box
			sx={mergeSx(
				layout.spanAllColumns ?
					{ gridColumn: "1 / -1" }
				:	{
						gridColumnStart: layout.placeOnNewRow ? `1` : "auto",
						gridColumnEnd: `span ${layout.columnSpan ?? 1}`,
					},
				sx
			)}
			onContextMenu={(e) => {
				if (contextMenu == null) return;
				openContextMenu({
					mouseEvent: e,
					content: contextMenu(),
				});
			}}
		>
			{children}
			<AavoContextMenu state={contextMenuState} />
		</Box>
	);
};

const ConfiguratorComponentInner = (props: ConfiguratorComponentProps) => {
	const { apiRef, component, onSubmit: onSubmitProp, disabled } = props;

	const [componentValue, setComponentValue] = useState<ConfiguratorInput_InputValue>(component.currentValue);

	useEffect(() => {
		apiRef({
			setValue: setComponentValue,
		});
	}, [setComponentValue, apiRef]);

	const onSubmit = async (newValue: ConfiguratorInput_InputValue_Scalar) => {
		setComponentValue(newValue);
		await onSubmitProp(newValue);
	};

	const componentWithLiveValue = setComponentCurrentValue(component, componentValue);

	switch (componentWithLiveValue.type) {
		case "field":
			return (
				<ConfiguratorFieldComponent
					field={componentWithLiveValue}
					onChange={setComponentValue}
					onSubmit={onSubmit}
					disabled={disabled}
				/>
			);
		case "text":
			return <ConfiguratorTextComponent component={componentWithLiveValue} />;
		case "subConfigurator":
			return <></>;
		case "subConfiguratorList":
			return <></>;
	}
};
